import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NgbCalendar,
  NgbDate,
  NgbDatepicker,
  NgbDatepickerModule,
  NgbDateStruct,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-datepicker',
  standalone: true,
  imports: [
    NgbDatepicker,
    NgbDatepickerModule,
    FormsModule,
    TranslateModule,
    NgbDatepickerModule,
    CommonModule,
  ],
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.scss',
})
export class DatepickerComponent implements OnInit {
  @Output() dateRangeChanged = new EventEmitter<any>();
  @Output() close = new EventEmitter<void>();

  private el = inject(ElementRef);

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.el.nativeElement.contains(event.target)) {
      this.close.emit();
    }
  }
  @Input() inputFromDate?: Date;
  @Input() inputToDate?: Date;
  hoveredDate: NgbDate | null = null;
  fromDate: NgbDate | null = null;
  toDate: NgbDate | null = null;

  dateFrom: Date = new Date();
  dateTo: Date = new Date();
  isCalendarShown = false;
  maxDate: NgbDateStruct;

  @Input() scale: number = 1;
  @Input() range: boolean = false;

  constructor() {
    const today = new Date();
    this.maxDate = {
      year: today.getFullYear(),
      month: today.getMonth() + 1,
      day: today.getDate(),
    };
  }

  ngOnInit(): void {
    if (this.inputFromDate && this.inputToDate) {
      this.fromDate = new NgbDate(
        this.inputFromDate.getFullYear(),
        this.inputFromDate.getMonth() + 1,
        this.inputFromDate.getDate(),
      );
      this.toDate = new NgbDate(
        this.inputToDate.getFullYear(),
        this.inputToDate.getMonth() + 1,
        this.inputToDate.getDate(),
      );
    }
  }

  onDateSelection(date: NgbDate, withFormat: boolean = true) {
    if (this.range) {
      if (!this.fromDate || (this.fromDate && this.toDate)) {
        this.fromDate = date;
        this.toDate = null;
      } else if (this.fromDate && !this.toDate) {
        if (!date) {
          this.fromDate = null;
          this.toDate = date;
        } else if (this.fromDate.equals(date)) {
          this.fromDate = null;
          this.dateRangeChanged.emit({
            dateFrom: undefined,
            dateTo: undefined,
          });
        } else if (date.after(this.fromDate)) {
          this.toDate = date;
        } else {
          this.toDate = null;
        }
      }
    } else {
      this.fromDate = date;
      this.toDate = null;
    }

    if (withFormat && this.fromDate)
      this.formatDate(this.fromDate, this.toDate ?? this.fromDate);
  }

  formatDate(startDate: NgbDate, endDate: NgbDate): void {
    this.dateFrom = new Date(startDate.year, startDate.month - 1, startDate.day);
    this.dateTo = new Date(endDate.year, endDate.month - 1, endDate.day, 23,59,59,999);
    this.dateRangeChanged.emit({
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
    });
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDate &&
      !this.toDate &&
      this.hoveredDate &&
      date.after(this.fromDate) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isSelected(date: NgbDate): boolean {
    return (
      !this.range &&
      (this.fromDate ? true : false) &&
      date.equals(this.fromDate)
    );
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  toggleModal() {
    this.isCalendarShown = !this.isCalendarShown;
  }
}
